import React from "react"
import { Field } from "formik"
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import {
  Box,
  Grid,
  IconButton,
  Stack,
  Typography,
  useTheme
} from "@mui/material"
import useEmblaCarousel from "embla-carousel-react"

import useLocale from "hooks/useLocale"
import { HorizontalStack } from "shared/ui/berry-jass"
import { CheckboxGroup } from "shared/ui/berry-jass-formik"
import { SupplierIcon } from "entities/supplier"
import {
  SalesProductInfoPopover,
  getProductNameByLocale,
  getDiscountPrice
} from "entities/sales-product"

const RecommendedProduct = ({ showPackagePrice, onProductSelect, product }) => {
  const locale = useLocale()
  const productName = getProductNameByLocale(product, locale)
  const price = getDiscountPrice({ product, showPackagePrice })

  return (
    <Box
      p={2}
      sx={{
        borderRadius: "8px",
        border: "1px #DFDFDF solid"
      }}
    >
      <Grid container columnSpacing={2}>
        <Grid
          item
          md={2}
          display="grid"
          justifyContent="center"
          alignItems="center"
        >
          <SupplierIcon iconUrl={product.icon_url} />
        </Grid>
        <Grid item md={10}>
          <Stack gap={0}>
            <Stack gap={0.25}>
              <HorizontalStack gap={0.5}>
                <Typography
                  variant="h5"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                >
                  {product.supplier}
                </Typography>
                {product.runtime_in_days && (
                  <Typography variant="h5">
                    ({product.runtime_in_days})
                  </Typography>
                )}

                <SalesProductInfoPopover product={product} />
              </HorizontalStack>
              <Typography
                variant="subtitle2"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis"
                }}
              >
                {productName}
              </Typography>
            </Stack>
            <HorizontalStack justifyContent="space-between">
              <Typography variant="h4" fontWeight={900}>
                {price}
              </Typography>
              <Field
                component={CheckboxGroup}
                onBeforeChange={(checked) => onProductSelect(checked, product)}
                name="selected_products"
                value={product.id}
              />
            </HorizontalStack>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  )
}

export const RecommendedProducts = ({
  products,
  onProductSelect,
  showPackagePrice
}) => {
  const theme = useTheme()
  const [emblaRef, emblaApi] = useEmblaCarousel({ slidesToScroll: "auto" })
  const handleScrollPrev = () => {
    if (!emblaApi) return

    emblaApi.scrollPrev()
  }

  const handleScrollNext = () => {
    if (!emblaApi) return

    emblaApi.scrollNext()
  }

  if (!products?.length) {
    return null
  }

  return (
    <HorizontalStack gap={2} data-testid="recommendations">
      <IconButton onClick={handleScrollPrev}>
        <KeyboardArrowLeftIcon />
      </IconButton>
      <Box
        className="embla"
        sx={{
          flexGrow: 1,
          maxWidth: "calc(100vw - 180px)",
          "--slide-height": "19rem",
          "--slide-spacing": "1rem",
          [theme.breakpoints.down("sm")]: {
            "--slide-size": "calc(100% / 1)"
          },
          [theme.breakpoints.up("sm")]: {
            "--slide-size": "calc(100% / 2)"
          },
          [theme.breakpoints.up("md")]: {
            "--slide-size": "calc(100% / 3)"
          },
          [theme.breakpoints.up("lg")]: {
            "--slide-size": "calc(100% / 4)"
          }
        }}
      >
        <Box
          className="embla__viewport"
          ref={emblaRef}
          sx={{ overflow: "hidden" }}
        >
          <Box
            className="embla__container"
            sx={{
              display: "flex",
              touchAction: "pan-y pinch-zoom",
              marginLeft: "calc(var(--slide-spacing) * -1)"
            }}
          >
            {products.map((product) => (
              <Box
                className="embla__slide"
                key={product.id}
                sx={{
                  transform: "translate3d(0, 0, 0)",
                  flex: "0 0 var(--slide-size)",
                  minWidth: "0",
                  paddingLeft: "var(--slide-spacing)"
                }}
              >
                <RecommendedProduct
                  onProductSelect={onProductSelect}
                  showPackagePrice={showPackagePrice}
                  product={product}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <IconButton onClick={handleScrollNext}>
        <KeyboardArrowRightIcon />
      </IconButton>
    </HorizontalStack>
  )
}
